package com.anjuros.website.redesign.components

import androidx.compose.runtime.Composable
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.redesign.styles.RdBreadcrumbStyle
import com.anjuros.website.redesign.util.RdRoute
import com.anjuros.website.redesign.util.RdText
import com.anjuros.website.redesign.util.SITE_URL
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Text

/** A page between Home and the current page in the breadcrumb, such as Blog above an article. */
class RdCrumb(val text: RdText, val path: String)

/** "Home / Current page" trail shown above a subpage title. */
@Composable
fun RdBreadcrumb(current: RdText) {
    RdBreadcrumb(LocalText(current))
}

/** "Home / parents… / Current page" trail, for pages whose title is not an [RdText], like blog articles. */
@Composable
fun RdBreadcrumb(current: String, parents: List<RdCrumb> = emptyList()) {
    val label = LocalText(RdText.BREADCRUMB_LABEL)
    Nav({ attr("aria-label", label) }) {
        Ol(RdBreadcrumbStyle.toModifier().toAttrs()) {
            Li { RdLink(RdRoute.HOME) { Text(LocalText(RdText.BREADCRUMB_HOME)) } }
            parents.forEach { crumb -> Li { RdLink(crumb.path) { Text(LocalText(crumb.text)) } } }
            Li({ attr("aria-current", "page") }) { Text(current) }
        }
    }
}

/** BreadcrumbList JSON-LD matching [RdBreadcrumb], to drop into a page's structured data. */
@Composable
fun rdBreadcrumbJsonLd(current: RdText, path: String): String = rdBreadcrumbJsonLd(LocalText(current), path)

@Composable
fun rdBreadcrumbJsonLd(current: String, path: String, parents: List<RdCrumb> = emptyList()): String {
    val items = buildList {
        add(LocalText(RdText.BREADCRUMB_HOME) to "$SITE_URL/")
        parents.forEach { add(LocalText(it.text) to "$SITE_URL${it.path}") }
        add(current to "$SITE_URL$path")
    }
    val list = items.mapIndexed { index, (name, url) ->
        """{"@type":"ListItem","position":${index + 1},"name":${jsonString(name)},"item":"$url"}"""
    }.joinToString(",")
    return """{"@type":"BreadcrumbList","itemListElement":[$list]}"""
}
